import React, { useEffect, useState } from 'react';
import { Anchor, RefreshCw } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useHullStabilityFilter } from '../../../contexts/HullStabilityFilterContext';
import { requestHullStabilitySession } from '../../../utils/postUploadHullStability';
import { Badge } from '../../ui/Badge';
import HullSetInfoPopover from './HullSetInfoPopover';

interface HullStabilityPanelProps {
  sessionId: string | null;
}

function formatMetric(value: number, suffix = '') {
  if (!Number.isFinite(value)) return '—';
  return `${value}${suffix}`;
}

const RECOMPUTE_NOTICE_MS = 5000;

const HullStabilityPanel: React.FC<HullStabilityPanelProps> = ({ sessionId }) => {
  const { session } = useAuth();
  const { setStrokesOnly, setSetStrokesOnly, hasData, isLoading, comparison, refetchStability } =
    useHullStabilityFilter();
  const [isRecomputing, setIsRecomputing] = useState(false);
  const [recomputeNotice, setRecomputeNotice] = useState<string | null>(null);
  const [recomputeError, setRecomputeError] = useState<string | null>(null);

  useEffect(() => {
    setRecomputeNotice(null);
    setRecomputeError(null);
    setIsRecomputing(false);
  }, [sessionId]);

  useEffect(() => {
    if (!recomputeNotice) return undefined;
    const timer = window.setTimeout(() => setRecomputeNotice(null), RECOMPUTE_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [recomputeNotice]);

  if (!sessionId) return null;

  const handleRecompute = async () => {
    setIsRecomputing(true);
    setRecomputeNotice(null);
    setRecomputeError(null);

    const result = await requestHullStabilitySession(sessionId, session?.access_token);

    setIsRecomputing(false);

    if (result.ok === true) {
      setRecomputeNotice(`${result.cleanCount} of ${result.rowsWritten} strokes marked set`);
      await refetchStability();
    } else {
      setRecomputeError(result.message);
    }
  };

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-border">
      <div className="px-4 sm:px-6 py-4 border-b border-border flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-content-primary flex items-center gap-2">
            <Anchor className="h-5 w-5 text-rowing-blue flex-shrink-0" aria-hidden />
            Hull Set
          </h3>
          <p className="text-sm text-content-secondary mt-1 max-w-2xl">
            Marks strokes where hull roll stayed under fixed limits (pre-catch, drive, at catch).
            Toggle <strong>Set strokes only</strong> to hide strokes taken while the boat was too
            wobbly for fair gate/power analysis.
          </p>
        </div>
        <div className="flex items-start gap-2">
          <button
            type="button"
            onClick={() => void handleRecompute()}
            disabled={isRecomputing}
            className="inline-flex items-center gap-1.5 text-sm text-content-muted hover:text-content-secondary disabled:opacity-50"
            aria-label="Recompute hull set scores"
          >
            <RefreshCw className={`h-4 w-4 ${isRecomputing ? 'animate-spin' : ''}`} />
            Recompute
          </button>
          <button
            type="button"
            onClick={() => void refetchStability()}
            disabled={isLoading}
            className="inline-flex items-center gap-1.5 text-sm text-content-muted hover:text-content-secondary disabled:opacity-50"
            aria-label="Refresh hull set display"
          >
            Refresh
          </button>
          <HullSetInfoPopover />
        </div>
      </div>

      <div className="px-4 sm:px-6 py-4 space-y-4">
        {recomputeError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            {recomputeError}
          </div>
        )}

        {recomputeNotice && !recomputeError && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
            {recomputeNotice}
          </div>
        )}

        {isLoading && !hasData && (
          <p className="text-sm text-content-secondary">Loading hull set analysis…</p>
        )}

        {!isLoading && !hasData && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Hull set scores are not ready yet. Upload a Peach session with boat motion data, then
            click <strong>Recompute</strong>, or wait for automatic scoring after upload.
          </div>
        )}

        {hasData && comparison && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-content-primary">
                <span className="font-semibold">{comparison.setCount}</span> of{' '}
                <span className="font-semibold">{comparison.totalScored}</span> strokes are{' '}
                <Badge variant="info">Set</Badge>
                <span className="text-content-secondary ml-2">
                  ({comparison.notSetCount} not set)
                </span>
              </p>
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-border text-rowing-blue focus:ring-rowing-blue"
                  checked={setStrokesOnly}
                  onChange={(event) => setSetStrokesOnly(event.target.checked)}
                />
                <span className="text-sm font-medium text-content-primary">Set strokes only</span>
              </label>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-border rounded-lg overflow-hidden">
                <thead className="bg-surface-muted">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-content-secondary"> </th>
                    <th className="text-right px-3 py-2 font-medium text-content-secondary">
                      All strokes
                    </th>
                    <th className="text-right px-3 py-2 font-medium text-content-secondary">
                      Set only
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-border">
                    <td className="px-3 py-2 text-content-secondary">Stroke count</td>
                    <td className="px-3 py-2 text-right font-medium">{comparison.totalScored}</td>
                    <td className="px-3 py-2 text-right font-medium text-rowing-blue">
                      {comparison.setCount}
                    </td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="px-3 py-2 text-content-secondary">Avg crew power</td>
                    <td className="px-3 py-2 text-right">
                      {formatMetric(comparison.allMetrics.crewAverage, ' W')}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-rowing-blue">
                      {formatMetric(comparison.setMetrics.crewAverage, ' W')}
                    </td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="px-3 py-2 text-content-secondary">Consistency (CV%)</td>
                    <td className="px-3 py-2 text-right">
                      {formatMetric(comparison.allMetrics.consistency, '%')}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-rowing-blue">
                      {formatMetric(comparison.setMetrics.consistency, '%')}
                    </td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="px-3 py-2 text-content-secondary">Peak power</td>
                    <td className="px-3 py-2 text-right">
                      {formatMetric(comparison.allMetrics.peakPower, ' W')}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-rowing-blue">
                      {formatMetric(comparison.setMetrics.peakPower, ' W')}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HullStabilityPanel;
